import {Dayjs} from 'dayjs';
import {z as r} from 'zod';

import {generateResponseSchema} from '../api/api-type';

export enum CommonPromotionStatusEnum {
    Deleted = 'deleted',
    Draft = 'draft',
    Planned = 'planned',
    InProgress = 'in_progress',
    Sent = 'sent',
    Paused = 'paused',
}

export enum PromotionCatalogStatusEnum {
    Unknown = '',
    Active = 'active',
    Planned = 'planned',
    Finished = 'finished',
    Deleted = 'deleted',
    Paused = 'paused',
    OnModeration = 'on_moderation',
    Rejected = 'rejected',
}

const promotionListItemSchema = r.object({
    id: r.number(),
    title: r.string(),
    status: r.nativeEnum(CommonPromotionStatusEnum),
    preview: r.object({id: r.number(), url: r.string()}).nullable(),
    companiesCount: r.number(),
    createdAt: r.string().nullable(),
    updatedAt: r.string().nullable(),
    publishAt: r.string().nullable(),
    deletedAt: r.string().nullable(),
    startAt: r.string().nullable(),
    finishAt: r.string().nullable(),
});

export type PromotionListItemType = r.infer<typeof promotionListItemSchema>;

export const promotionsListResponseSchema = generateResponseSchema(promotionListItemSchema);

export type PromotionsListResponseType = r.infer<typeof promotionsListResponseSchema>;

const promotionImageSchema = r.object({
    id: r.number(),
    url: r.string(),
});

type PromotionImageType = r.infer<typeof promotionImageSchema>;

export const promotionDetailSchema = promotionListItemSchema.merge(
    r.object({
        description: r.string(),
        url: r.string(),
        companiesIds: r.array(r.number()),
        catalogsIds: r.array(r.number()),
        images: r.array(promotionImageSchema),
    })
);

export type PromotionDetailType = r.infer<typeof promotionDetailSchema>;

const promotionSelectedCompanySchema = r.object({
    id: r.number(),
    status: r.nativeEnum(PromotionCatalogStatusEnum),
    name: r.string(),
    address: r.string().nullable(),
    companyCode: r.string().optional(),
});

export type PromotionSelectedCompanyType = r.infer<typeof promotionSelectedCompanySchema>;

export const promotionSelectedCompaniesSchema = generateResponseSchema(promotionSelectedCompanySchema);

export type PromotionSelectedCompaniesType = r.infer<typeof promotionSelectedCompaniesSchema>;

export const uploadPromotionImageResponseSchema = r.object({
    id: r.number(),
});

export type UploadPromotionImageResponseType = r.infer<typeof uploadPromotionImageResponseSchema>;

// Payload sent to the API (camelCase; `serialize` converts it to snake_case).
// Companies are resolved server-side from the posts companies-selector via `selectorId`.
export type PromotionRequestType = {
    title: string;
    description: string;
    url: string;
    selectorId: string;
    catalogsIds: Array<number>;
    publishAt: string | null;
    deletedAt: string | null;
    startAt: string;
    finishAt: string;
    imageIds: Array<number>;
    status: CommonPromotionStatusEnum;
};

export enum PromotionFormFieldsEnum {
    Title = 'title',
    Description = 'description',
    Url = 'url',
    Images = 'images',
    Period = 'period',
}

// The promotion's validity period as rendered by a single range picker.
type PromotionPeriodType = [Dayjs | null, Dayjs | null] | null;

// Form shape. Selected companies live in the companies-selector (not the form), the form only keeps
// the brand and the mirrored selected count so both can be validated.
export type PromotionFormType = {
    title: string;
    description?: string;
    url?: string;
    images: Array<PromotionImageType>;
    period: PromotionPeriodType;
    brandId?: number | null;
    companiesCount?: number;
    // Carried through untouched: no longer editable, but an update must not wipe them.
    publishAt?: Dayjs | null;
    deletedAt?: Dayjs | null;
};
